import React from "react";
import { motion } from "framer-motion";
import { imgUrl } from "@/lib/api";
import {
  MapPin, Landmark, Coffee, Utensils, Shirt, Music, Palette, Building2, PartyPopper,
  Mountain, Droplets, Wheat, Factory, Cpu, Zap, Plane, GraduationCap, Users, Sparkles,
  ArrowDown, ExternalLink, TrendingUp, Sprout, Globe,
} from "lucide-react";

const IMG = {
  highlands: "https://images.unsplash.com/photo-1572888195250-3037a59d3578?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  coffee: "https://images.unsplash.com/photo-1630861412229-67e2acb44b7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  city: "https://images.unsplash.com/photo-1734865934450-719ef6f59a37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  fabrics: "https://images.unsplash.com/photo-1599303000936-1cf21eac4456?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  people: "https://images.unsplash.com/photo-1764145162259-04eaf2b3d86a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const GOLD = "#C79A4B";
const DARK = "#14241C";

const facts = [
  { label: "Official Name", value: "Federal Democratic Republic of Ethiopia" },
  { label: "Capital", value: "Addis Ababa" },
  { label: "Location", value: "Horn of Africa" },
  { label: "Currency", value: "Ethiopian Birr (ETB)" },
  { label: "Country Code", value: "+251" },
  { label: "Internet Domain", value: ".et" },
];

const stats = [
  { value: "~126.5M", label: "Population", src: "World Bank", year: "2023" },
  { value: "1,104,300 km²", label: "Land area", src: "Britannica", year: "2024" },
  { value: "12 + 2", label: "Regional states + city administrations", src: "FDRE", year: "2023" },
  { value: "~22%", label: "Population living in urban areas", src: "World Bank", year: "2023" },
];

const timeline = [
  { era: "~3.2M years ago", title: "Human Origins", body: "The Australopithecus afarensis fossil known worldwide as Lucy — and in Ethiopia as Dinkinesh — was discovered in the Afar region, placing Ethiopia at the heart of the study of early human history." },
  { era: "Antiquity", title: "Ancient Civilizations", body: "Early organized societies and trade networks developed across the region, laying foundations for one of Africa's oldest continuous civilizations." },
  { era: "c. 1st–7th c.", title: "Kingdom of Aksum", body: "A major center of international trade, political power, culture, its own currency, monumental architecture, and early religion." },
  { era: "12th–16th c.", title: "Medieval Ethiopia", body: "The Zagwe dynasty and the rock-hewn churches of Lalibela; the Solomonic dynasty, regional kingdoms, religion, art, architecture and trade." },
  { era: "17th–18th c.", title: "Gondarine Period", body: "Gondar and the castles of Fasil Ghebbi — a flourishing of architecture, art and political development." },
  { era: "19th c.", title: "Consolidation & Modernization", body: "Political consolidation, early modernization under Menelik II, and the founding and growth of Addis Ababa." },
  { era: "1896", title: "Battle of Adwa", body: "Ethiopia's victory over invading Italian forces — a landmark moment in Ethiopian and African history and a symbol of sovereignty." },
  { era: "1936–1941", title: "Occupation & Resistance", body: "A period of Italian occupation met by sustained Ethiopian resistance until liberation." },
  { era: "20th c.", title: "Transformation", body: "Haile Selassie and modernization, Ethiopia's diplomatic role in Africa, the 1974 revolution, the Derg period, and profound political and social change." },
  { era: "1991 →", title: "Transition", body: "A political and economic transition into the modern federal era." },
  { era: "Today", title: "Ethiopia Now", body: "A nation balancing ancient heritage with a fast-evolving contemporary society." },
];

const peoples = ["Oromo", "Amhara", "Somali", "Tigrayan", "Sidama", "Gurage", "Wolayta", "Afar", "Harari", "Hadiya", "Gamo", "and many more"];
const languages = ["Afaan Oromo", "Amharic", "Somali", "Tigrinya", "Sidama", "Afar", "Wolaytta", "Harari", "Gurage languages", "and others"];

const culture = [
  { icon: Coffee, title: "Ethiopian Coffee", body: "The birthplace of coffee — from farming and roasting to the celebrated coffee ceremony." },
  { icon: Utensils, title: "Food", body: "Injera, teff, rich stews, distinctive spices and diverse regional culinary traditions." },
  { icon: Shirt, title: "Clothing & Fabrics", body: "Handwoven textiles, embroidery, patterns and craftsmanship that vary by region." },
  { icon: Music, title: "Music", body: "Traditional and contemporary sounds across many communities." },
  { icon: PartyPopper, title: "Dance", body: "Distinct regional dance traditions and celebrations." },
  { icon: Palette, title: "Art & Craft", body: "Painting, textiles, pottery, jewelry, basketry and woodwork." },
  { icon: Building2, title: "Architecture", body: "Aksum, Lalibela, Gondar, Harar, traditional homes and modern Addis Ababa." },
  { icon: Sparkles, title: "Festivals", body: "Major celebrations and traditions observed across the country." },
];

const geography = ["Ethiopian Highlands", "Great Rift Valley", "Simien Mountains", "Bale Mountains", "Afar Depression", "Lakes & Rivers", "Agricultural regions", "Drylands", "Forests", "Cities"];
const resources = ["Agricultural land", "Water resources", "Livestock", "Coffee", "Crops", "Renewable-energy potential", "Minerals", "Forest resources", "Biodiversity"];
const crops = ["Coffee", "Legumes", "Oilseeds", "Cereals", "Potatoes", "Sugarcane", "Vegetables", "Teff", "Enset"];
const sectors = [
  { icon: Wheat, t: "Agriculture" }, { icon: Factory, t: "Manufacturing" }, { icon: Users, t: "Services" },
  { icon: Building2, t: "Construction" }, { icon: Globe, t: "Trade" }, { icon: Plane, t: "Transportation" },
  { icon: MapPin, t: "Tourism" }, { icon: Cpu, t: "Technology" }, { icon: Zap, t: "Energy" },
  { icon: Sprout, t: "Small Business" }, { icon: TrendingUp, t: "Exports" }, { icon: Sparkles, t: "Entrepreneurship" },
];

const childDimensions = ["Housing", "Sanitation", "Water", "Nutrition", "Health", "Education", "Protection", "Information", "Leisure"];

const strengths = [
  { t: "People", d: "Human knowledge, creativity, labor, entrepreneurship and leadership." },
  { t: "Youth", d: "A new generation of students, professionals, farmers, creators and leaders." },
  { t: "Agriculture", d: "Existing farming knowledge, crops, livestock and production." },
  { t: "Coffee", d: "An internationally recognized product and cultural tradition." },
  { t: "Culture", d: "Food, music, art, history, textiles, architecture and traditions." },
  { t: "Entrepreneurship", d: "Small businesses and emerging enterprises." },
  { t: "Diaspora", d: "Knowledge, investment, expertise and global market connections." },
  { t: "Geography", d: "Diverse landscapes, ecosystems and agricultural environments." },
  { t: "Renewable Energy", d: "Hydropower, solar, wind and other potential resources." },
  { t: "Tourism", d: "History, culture, architecture and extraordinary landscapes." },
];

const opportunities = ["Agriculture", "Food processing", "Manufacturing", "Technology", "Digital education", "Renewable energy", "Infrastructure", "Construction", "Tourism", "Hospitality", "Transportation", "Logistics", "Creative industries", "Textiles", "Fashion", "Coffee", "Small business", "Entrepreneurship", "Education", "Skills development", "International trade", "Diaspora investment"];

const challengeOpp = [
  { t: "Water Access", c: "Limited reliable access in some communities.", o: "Water infrastructure, purification, conservation, maintenance and local technical skills." },
  { t: "Youth Employment", c: "Young people need pathways into productive employment.", o: "Education, vocational training, entrepreneurship, technology and business development." },
  { t: "Agriculture", c: "Farmers may face productivity, climate, storage, transport or market-access barriers.", o: "Irrigation, technology, processing, storage, transport and stronger market connections." },
  { t: "Digital Access", c: "Connectivity and technology access remain unequal.", o: "Digital education, connectivity, computer skills, online business and tech careers." },
  { t: "Local Production", c: "Raw products can leave communities without capturing full value.", o: "Processing, manufacturing, packaging, branding and distribution can add economic activity." },
];

const challenges = ["Child Poverty", "Food Security", "Water & Sanitation", "Education", "Health", "Housing", "Employment", "Infrastructure", "Digital Access", "Displacement", "Climate & Drought"];

const sources = ["UNICEF", "World Bank", "African Development Bank", "United Nations agencies", "WHO", "FAO", "WFP", "Ethiopian statistical & government sources", "Academic institutions", "Britannica", "Historical scholarship"];

const Overline = ({ children, className = "" }) => (
  <p className={`text-xs tracking-[0.25em] uppercase font-bold ${className}`}>{children}</p>
);

const Chips = ({ items, dark }) => (
  <div className="flex flex-wrap gap-2.5">
    {items.map((t) => (
      <span key={t} className={`rounded-full px-4 py-2 text-sm ${dark ? "bg-white/8 border border-[#C79A4B]/30 text-[#F3ECDD]" : "bg-white border border-[#14241C]/10 text-[#14241C]"}`}>{t}</span>
    ))}
  </div>
);

export default function UnderstandingEthiopiaPage() {
  return (
    <div data-testid="understanding-ethiopia-page" className="bg-[#FAF7F0] text-[#14241C]">
      {/* 1 HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img src={IMG.highlands} alt="The Ethiopian highlands" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1a14] via-[#0d1a14]/70 to-[#0d1a14]/30" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-white py-24">
          <Overline className="text-[#E0A526] mb-5">Understanding Ethiopia</Overline>
          <h1 className="font-display text-5xl sm:text-7xl font-bold tracking-tight leading-[0.95]">Understanding the Past. Recognizing the Present. Looking Toward the Future.</h1>
          <p className="text-lg text-white/85 mt-8 max-w-2xl leading-relaxed">Ethiopia is a country of extraordinary history, cultural diversity, resilience, natural beauty, resources, challenges, and opportunity. Understanding it requires looking beyond any single story.</p>
          <p className="text-white/70 mt-4 max-w-2xl leading-relaxed">It means understanding where the country has been, who its people are, how communities live today, what challenges remain, what strengths already exist, and what the future may hold.</p>
          <a href="#glance" className="mt-10 inline-flex items-center gap-2 bg-[#C79A4B] text-[#14241C] rounded-full px-7 py-3.5 font-bold hover:bg-[#b6893c] transition-colors">Explore Ethiopia <ArrowDown size={18} /></a>
        </div>
      </section>

      {/* 2 AT A GLANCE */}
      <section id="glance" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Overline className="text-[#B45B3E] mb-3">Ethiopia at a glance</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-12">Discover Ethiopia.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {facts.map((f) => (
            <div key={f.label} className="bg-white border border-[#14241C]/10 rounded-2xl p-6">
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#B45B3E] mb-2">{f.label}</p>
              <p className="font-display text-xl font-semibold">{f.value}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s) => (
            <div key={s.label} className="bg-[#14241C] text-white rounded-2xl p-6">
              <p className="font-display text-3xl font-bold" style={{ color: GOLD }}>{s.value}</p>
              <p className="text-sm text-white/75 mt-2 leading-snug">{s.label}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40 mt-3">Source · {s.src} ({s.year})</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3 TIMELINE */}
      <section className="bg-[#14241C] text-[#F3ECDD]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#E0A526] mb-3">Ethiopia through time</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">A country with an extraordinary history.</h2>
          <p className="text-[#F3ECDD]/70 max-w-2xl mb-14">Thousands of years of ancient civilizations, kingdoms, trade, faith, resistance, and change — context for understanding Ethiopia today.</p>
          <div className="relative border-l-2 border-[#C79A4B]/30 ml-3">
            {timeline.map((e, i) => (
              <motion.div key={e.title} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="relative pl-8 pb-10 last:pb-0">
                <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full" style={{ background: GOLD }} />
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#E0A526]">{e.era}</p>
                <h3 className="font-display text-2xl font-semibold mt-1 mb-2">{e.title}</h3>
                <p className="text-[#F3ECDD]/75 leading-relaxed">{e.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 PEOPLE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Overline className="text-[#B45B3E] mb-3">The people of Ethiopia</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-6">Many peoples. Many languages. One Ethiopia.</h2>
          <p className="text-[#4a5a50] leading-relaxed mb-6">Ethiopia is home to more than 80 ethnic groups, each with distinct histories, languages, traditions, foods, music, clothing and identities — no single culture represents all Ethiopians.</p>
          <Chips items={peoples} />
        </div>
        <img src={imgUrl(IMG.people)} alt="Ethiopians in traditional dress celebrating together" loading="lazy" className="w-full h-[520px] object-cover rounded-3xl shadow-xl" />
      </section>

      {/* 5 LANGUAGES */}
      <section className="bg-[#FBF0E2]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <Overline className="text-[#B45B3E] mb-3">A country of many voices</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Languages.</h2>
          <p className="text-[#4a5a50] max-w-2xl mb-8">Amharic, Oromo, Somali, Tigrinya and Afar are among Ethiopia's official languages, with many more spoken across regions — language is woven into identity, family, history and culture.</p>
          <Chips items={languages} />
        </div>
      </section>

      {/* 6 CULTURE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Overline className="text-[#B45B3E] mb-3">A living heritage</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-12">Culture & heritage.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {culture.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
              className="bg-white border border-[#14241C]/10 rounded-2xl p-6 hover:-translate-y-1 transition-transform">
              <span className="w-11 h-11 rounded-full flex items-center justify-center mb-4" style={{ background: "#B45B3E14", color: "#B45B3E" }}><c.icon size={21} /></span>
              <h3 className="font-display text-lg font-semibold mb-1">{c.title}</h3>
              <p className="text-sm text-[#4a5a50] leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-6 mt-8">
          <img src={IMG.coffee} alt="Ethiopian coffee ceremony" loading="lazy" className="w-full h-72 object-cover rounded-3xl" />
          <img src={imgUrl(IMG.fabrics)} alt="Hands weaving traditional Ethiopian textiles on a loom" loading="lazy" className="w-full h-72 object-cover rounded-3xl" />
        </div>
      </section>

      {/* 7 GEOGRAPHY */}
      <section className="bg-[#14241C] text-[#F3ECDD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#E0A526] mb-3">A country of extraordinary landscapes</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-6 flex items-center gap-3"><Mountain size={40} style={{ color: GOLD }} /> Land & geography.</h2>
          <p className="text-[#F3ECDD]/70 max-w-2xl mb-8">Geography shapes agriculture, water, transportation, climate, livelihoods, settlement and economic opportunity across the country.</p>
          <Chips items={geography} dark />
        </div>
      </section>

      {/* 8 RESOURCES + 9 AGRICULTURE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14">
        <div>
          <Overline className="text-[#B45B3E] mb-3">Understanding what the country has</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Natural resources.</h2>
          <p className="text-[#4a5a50] leading-relaxed mb-6">Having a resource does not automatically create prosperity — infrastructure, investment, skills, governance, markets, technology, processing and sustainability all shape how resources benefit communities.</p>
          <Chips items={resources} />
        </div>
        <div>
          <Overline className="text-[#B45B3E] mb-3">From the land</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Agriculture.</h2>
          <p className="text-[#4a5a50] leading-relaxed mb-5">Agriculture is deeply tied to Ethiopian livelihoods — from farming, livestock and coffee to food production, small farmers, markets, processing and exports.</p>
          <Chips items={crops} />
          <p className="text-sm font-semibold mt-6 mb-2">Challenges include:</p>
          <p className="text-sm text-[#4a5a50]">Drought · water access · soil conditions · market access · storage · transportation · climate variability · productivity.</p>
        </div>
      </section>

      {/* 10 ECONOMY */}
      <section className="bg-[#FBF0E2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#B45B3E] mb-3">Understanding Ethiopia's economy</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">The economy.</h2>
          <p className="text-[#4a5a50] max-w-2xl mb-12">Ethiopia has seen periods of rapid growth while continuing to face structural development challenges and poverty — both realities are true at once.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {sectors.map((s) => (
              <div key={s.t} className="bg-white border border-[#14241C]/10 rounded-2xl p-5 text-center">
                <s.icon size={22} className="mx-auto mb-2" style={{ color: "#B45B3E" }} />
                <p className="text-sm font-semibold">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11 MODERN ETHIOPIA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <img src={IMG.city} alt="Modern Addis Ababa skyline" loading="lazy" className="w-full h-auto rounded-3xl shadow-xl order-1" />
        <div className="order-2">
          <Overline className="text-[#B45B3E] mb-3">Tradition meets transformation</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-6">Modern Ethiopia.</h2>
          <p className="text-[#4a5a50] leading-relaxed mb-4">Addis Ababa's skyline, universities, young professionals, technology, factories, transportation, entrepreneurs, fashion, music and modern agriculture tell another side of the story.</p>
          <p className="font-display text-2xl font-bold">Ethiopia is not frozen in the past. <span className="text-[#B45B3E]">Its ancient heritage lives alongside a society that keeps evolving.</span></p>
        </div>
      </section>

      {/* 12 CHALLENGES */}
      <section className="bg-[#14241C] text-[#F3ECDD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#E0A526] mb-3">Progress and challenges can exist together</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Understanding the challenges.</h2>
          <p className="text-[#F3ECDD]/70 max-w-2xl mb-10">Development challenges are real and worth understanding objectively — presented as categories, with figures shown only alongside their year and source.</p>
          <div className="flex flex-wrap gap-2.5 mb-10">{challenges.map((c) => <span key={c} className="bg-white/8 border border-[#C79A4B]/30 rounded-full px-4 py-2 text-sm">{c}</span>)}</div>
          <div className="bg-white/5 border border-[#C79A4B]/25 rounded-2xl p-7 max-w-2xl">
            <p className="font-display text-4xl font-bold" style={{ color: GOLD }}>80.4%</p>
            <p className="text-[#F3ECDD]/85 mt-2">of children experience multidimensional poverty (deprivation in five or more areas of well-being).</p>
            <a href="https://www.unicef.org/ethiopia" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm mt-3 text-[#E0A526] hover:underline">Source · UNICEF, 2024 <ExternalLink size={13} /></a>
          </div>
        </div>
      </section>

      {/* 13 CHILDREN */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Overline className="text-[#B45B3E] mb-3">Understanding childhood in Ethiopia</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Children & families.</h2>
        <p className="text-[#4a5a50] max-w-2xl mb-10">A child's experience can differ greatly by location, household income, family circumstances, education, gender, water, nutrition, healthcare, housing, displacement and rural or urban environment.</p>
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-3 mb-10">
          {childDimensions.map((d) => (
            <div key={d} className="bg-[#FBF0E2] border border-[#14241C]/10 rounded-xl py-4 px-2 text-center">
              <p className="text-sm font-semibold">{d}</p>
            </div>
          ))}
        </div>
        <p className="font-display text-3xl sm:text-4xl font-bold text-[#B45B3E]">Poverty is more than income.</p>
        <p className="text-xs text-[#7a8a80] mt-3">Child-poverty framework · UNICEF, Multidimensional Child Poverty in Ethiopia, 2024.</p>
      </section>

      {/* 14 STRENGTHS */}
      <section className="bg-gradient-to-b from-[#1F4D2E] to-[#2c6b41] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#E0A526] mb-3">Challenges do not define a country</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-12">Ethiopia's strengths.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {strengths.map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="bg-white/10 border border-white/15 rounded-2xl p-6 backdrop-blur">
                <h3 className="font-display text-xl font-semibold mb-1" style={{ color: "#FCE8C0" }}>{s.t}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 15 OPPORTUNITY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Overline className="text-[#B45B3E] mb-3">Where challenges and possibilities meet</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">What could Ethiopia's future look like?</h2>
        <p className="text-[#4a5a50] max-w-2xl mb-8">These are areas of potential and opportunity — not predictions.</p>
        <Chips items={opportunities} />
      </section>

      {/* 16 CHALLENGE + OPPORTUNITY */}
      <section className="bg-[#FBF0E2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Overline className="text-[#B45B3E] mb-3">Possible opportunities</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-12">Challenge + opportunity.</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {challengeOpp.map((x) => (
              <div key={x.t} className="bg-white rounded-2xl overflow-hidden border border-[#14241C]/10">
                <div className="p-6 border-b border-[#14241C]/10">
                  <h3 className="font-display text-2xl font-semibold mb-3">{x.t}</h3>
                  <p className="text-xs tracking-[0.12em] uppercase font-bold text-[#B45B3E] mb-1">Challenge</p>
                  <p className="text-sm text-[#4a5a50]">{x.c}</p>
                </div>
                <div className="p-6 bg-[#1F4D2E]/5">
                  <p className="text-xs tracking-[0.12em] uppercase font-bold text-[#1F4D2E] mb-1">Possible opportunity</p>
                  <p className="text-sm text-[#4a5a50]">{x.o}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 17 REGIONS */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
        <Overline className="text-[#B45B3E] mb-3">One country. Different realities.</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Explore Ethiopia by region.</h2>
        <p className="text-[#4a5a50] max-w-2xl mx-auto mb-8">Each region has its own people, languages, culture, history, geography, cities, agriculture, economy, challenges, strengths and opportunities — an educational map of Ethiopia, not a map of poverty. Regional exploration is coming soon.</p>
        <Chips items={["Addis Ababa", "Oromia", "Amhara", "Tigray", "Somali", "Afar", "Sidama", "South Ethiopia", "Central Ethiopia", "Harari", "Gambela", "Benishangul-Gumuz", "Dire Dawa"]} />
      </section>

      {/* 18 PAST PRESENT FUTURE */}
      <section className="bg-[#14241C] text-[#F3ECDD]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-14">Past → Present → Future</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "Past", items: ["History", "Civilizations", "Culture", "Traditions", "Lessons"] },
              { t: "Present", items: ["People", "Resources", "Economy", "Challenges", "Progress"] },
              { t: "Future", items: ["Education", "Opportunity", "Innovation", "Enterprise", "Infrastructure", "Development"] },
            ].map((col) => (
              <div key={col.t} className="bg-white/5 border border-[#C79A4B]/25 rounded-2xl p-7">
                <p className="font-display text-3xl font-bold mb-4" style={{ color: GOLD }}>{col.t}</p>
                <ul className="space-y-2">{col.items.map((it) => <li key={it} className="text-[#F3ECDD]/80 text-sm flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />{it}</li>)}</ul>
              </div>
            ))}
          </div>
          <p className="text-center text-[#F3ECDD]/75 max-w-2xl mx-auto mt-12 leading-relaxed">Understanding where a country has been helps us better understand where it is — and imagine where it can go.</p>
        </div>
      </section>

      {/* 19 SOURCES */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
        <Overline className="text-[#B45B3E] mb-3">Learn from reliable information</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Research & sources.</h2>
        <p className="text-[#4a5a50] max-w-2xl mb-8">This page draws on credible institutions. Encyclopedic references are useful starting points, but important statistics should be verified against primary sources.</p>
        <Chips items={sources} />
      </section>

      {/* 20 FINAL */}
      <section className="relative overflow-hidden text-white">
        <img src={IMG.highlands} alt="Ethiopia" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0d1a14]/85" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-28 text-center">
          <Overline className="text-[#E0A526] mb-6">Understanding Ethiopia</Overline>
          <p className="text-lg text-white/85 leading-relaxed mb-4">Ethiopia cannot be understood through a single statistic, photograph, challenge, culture, or moment in history. It is ancient and modern. Rural and urban. Traditional and innovative.</p>
          <p className="text-white/75 leading-relaxed mb-4">It holds extraordinary cultural and historical wealth while continuing to confront significant development challenges. Its past provides context. Its present reveals both progress and challenges. Its people and resources reveal possibility. And its future remains unwritten.</p>
          <p className="font-display text-3xl sm:text-4xl font-bold mt-10 leading-tight">Understand the past.<br />Recognize the present.<br /><span style={{ color: GOLD }}>Imagine the possibilities.</span></p>
        </div>
      </section>
    </div>
  );
}
